import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';
import FileSaver from 'file-saver';
import { format } from 'date-fns';

import type { IResourceDatabaseRecord, IResourceDatabaseView, IResourceDatabaseViewItemsData } from '@/api/types';
import type { IDatabaseViewQueryParams } from '@/store/types';
import type { ITableSortingState } from '../table/types';
import type { RootState } from '@/store';
import type { ISelectedFolder } from '@/store/types';

import { Input, LoaderWrapper, PaginationSelectGroup, ResourcesNoData } from '@/components/common';
import { ResourceDatabaseItemsTable } from './ResourceDatabaseItemsTable';
import {
  getDatabaseView,
  getMemberList,
  previewDatabaseViewItem,
  setDatabaseViewQueryParams,
} from '@/store/slices/resourcesSlice';
import { selectAuthMenuType, selectUserId, selectUserType } from '@/store/selectors/authSelectors';
import {
  useAppDispatch,
  useAppSelector,
  useDynamicTableSort,
  useResourceNavigation,
  useSelectedItems,
  useTimezone,
} from '@/hooks';
import { DeleteBlue, Download, Search } from '@/assets/icons';
import {
  selectDatabaseViewQueryParams,
  selectFoldersList,
  selectPendingAction,
} from '@/store/selectors/resourcesSelectors';
import { AgentAccessLevel } from '@/api/enums';
import { UserType } from '@/store/enums';
import { DEBOUNCE_SEARCH_DELAY, RECORDS_TABLE_PAGE_SIZE } from '@/config/variables';
import { media } from '@/styles/theme';
import { ButtonBase } from '../Buttons';
import { otherColors } from '@/styles/colors';
import { debounce, exportViewData } from '@/helpers';
import { DATE_ISO_FORMAT } from '@/config/variables';
import { apiResources } from '@/api/resources';
import { combineClientMemberList, combineDatabaseAccessLevel } from '@/store/combiners/resourcesCombiners';
import { ResourceDatabaseTableSkeleton } from './ResourceDatabaseTable.skeleton';

const debouncedSearch = debounce(
  (
    search: string,
    queryParams: IDatabaseViewQueryParams,
    setQueryParams: (queryParams: IDatabaseViewQueryParams) => void,
  ) => {
    setQueryParams({ ...queryParams, pageNum: 1, text: search });
  },
  DEBOUNCE_SEARCH_DELAY,
);

const resourceDatabaseItemsContainerSelector = createSelector(
  selectUserId,
  selectAuthMenuType,
  selectUserType,
  selectFoldersList,
  selectPendingAction(getDatabaseView.typePrefix),
  selectPendingAction(getMemberList.typePrefix),
  (_: RootState, selectedFolder: ISelectedFolder) => selectedFolder,
  (userId, menuType, userType, foldersList, isViewPending, isMemberListPending, selectedFolder) => ({
    userId,
    menuType,
    userType,
    databaseAccessLevel: combineDatabaseAccessLevel(foldersList, selectedFolder),
    clientMemberList: combineClientMemberList(foldersList, selectedFolder),
    isPending: isViewPending || isMemberListPending,
  }),
);

interface IProps {
  items: IResourceDatabaseViewItemsData;
  view: IResourceDatabaseView;
  clientId: number;
  openEditItemModal: (item: IResourceDatabaseRecord) => void;
  openDeleteItemModal: (...itemId: number[]) => void;
}

const ResourceDatabaseItemsContainer = ({
  items,
  view,
  clientId,
  openEditItemModal,
  openDeleteItemModal,
}: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { selectedFolder } = useResourceNavigation();
  const { userId, databaseAccessLevel, menuType, userType, clientMemberList, isPending } = useAppSelector(state =>
    resourceDatabaseItemsContainerSelector(state, selectedFolder),
  );
  const dispatch = useAppDispatch();
  const { formatDateTime } = useTimezone();
  const { queryParams, setQueryParams } = useDatabaseViewQueryParams();

  const columnsToInclude = useMemo(() => view.props, [view.props]);
  const sortableColumns = useMemo(
    () => columnsToInclude.filter(column => column.field && !column.field.isInternal),
    [columnsToInclude],
  );

  const { selectedItems, toggleItem, toggleAll, clearSelection } = useSelectedItems<number>({
    items: items.Data?.map(item => item.Id) || [],
  });

  const getAccessType = useCallback(
    (item: IResourceDatabaseRecord): 'view' | 'edit' | 'delete' => {
      if (userType === UserType.client) return 'delete';

      const canDelete =
        databaseAccessLevel === AgentAccessLevel.ViewAllAndEditAll ||
        ([AgentAccessLevel.ViewAllAndEditOwn, AgentAccessLevel.ViewOwnAndEditOwn].includes(databaseAccessLevel) &&
          userId === item.OwnerIdInternal);

      if (!canDelete && menuType === UserType.client) {
        return 'edit';
      }

      return canDelete ? 'delete' : 'view';
    },
    [databaseAccessLevel, menuType, userId, userType],
  );

  const onSortingStateChange = useCallback<(sortingState: ITableSortingState<string>) => void>(
    sortingState => {
      const sort = Object.entries(sortingState).find(([, val]) => val !== undefined);
      if (!sort) return;
      const [sortBy, sortDirection] = sort;
      if (!sortDirection) return;

      setQueryParams({
        ...queryParams,
        pageNum: 1,
        sortBy,
        sortDirection,
      });
    },
    [queryParams, setQueryParams],
  );

  const { onSort, sortingState } = useDynamicTableSort({
    columns: sortableColumns,
    onSortingStateChange,
  });

  useEffect(() => {
    if (clientMemberList.length === 0) {
      const memberListRequest = dispatch(getMemberList({ clientId }));

      return () => memberListRequest.abort();
    }

    return;
  }, [dispatch, clientMemberList.length, clientId]);

  useEffect(() => {
    setQueryParams({
      text: '',
      pageNum: 1,
      sortBy: sortableColumns[0]?.field.columnName || '',
      sortDirection: 'ASC',
      pageSize: RECORDS_TABLE_PAGE_SIZE,
    });
  }, [setQueryParams, sortableColumns, view.id]);

  useEffect(() => {
    const getDatabaseViewCall = dispatch(
      getDatabaseView({
        clientId,
        viewId: view.id,
        dbId: view.rtxDatabase_Id,
        ...queryParams,
      }),
    );
    clearSelection();

    return () => getDatabaseViewCall.abort();
  }, [clearSelection, clientId, dispatch, sortableColumns, view.id, view.rtxDatabase_Id, queryParams]);

  const pageCount = Math.ceil(items.RecordCount / RECORDS_TABLE_PAGE_SIZE);

  const createOnPageChange = (action: 'increase' | 'decrease') => () => {
    switch (action) {
      case 'decrease': {
        if (queryParams.pageNum > 1) {
          setQueryParams({
            ...queryParams,
            pageNum: queryParams.pageNum - 1,
          });
        }
        break;
      }
      case 'increase':
        if (queryParams.pageNum < pageCount) {
          setQueryParams({
            ...queryParams,
            pageNum: queryParams.pageNum + 1,
          });
        }
    }
  };

  return (
    <>
      <ToolbarSection>
        <Input
          type="text"
          name="filterDatabase"
          className="resources-database-filter"
          placeholder={t`resources.leftPanel.placeholderSearch`}
          onChange={event => {
            debouncedSearch(event.target.value, queryParams, setQueryParams);
          }}
          isStandalone
        />
        <ToolbarSectionButtons>
          {userType === UserType.client && (
            <ActionButton
              icon={DeleteBlue}
              disabled={selectedItems.length === 0}
              onClick={() =>
                openDeleteItemModal(
                  ...selectedItems.filter(selectedId => items?.Data?.some(item => item.Id === selectedId)),
                )
              }
            >{t`resources.databases.deleteSelected`}</ActionButton>
          )}
          {menuType === UserType.client && (
            <>
              <ActionButton
                icon={Download}
                disabled={selectedItems.length === 0}
                onClick={() => {
                  const file = exportViewData(
                    items?.Data?.filter(item => selectedItems.includes(item.Id)) || [],
                    view,
                    formatDateTime,
                  );
                  if (file) {
                    FileSaver.saveAs(file, `${view.name}-${format(new Date(), DATE_ISO_FORMAT)}.csv`);
                  }
                }}
              >
                {t`resources.databases.exportSelected`}
              </ActionButton>
              <ActionButton
                icon={Download}
                onClick={async () => {
                  const { data } = await apiResources.getDatabaseViewExport({
                    dbId: view.rtxDatabase_Id,
                    viewId: view.id,
                    userType: UserType.client,
                    text: queryParams.text,
                    sortBy: queryParams.sortBy,
                    sortDirection: queryParams.sortDirection,
                  });

                  if (data) {
                    const file = new Blob([data], { type: 'text/csv;charset=utf-8' });
                    FileSaver.saveAs(file, `${view.name}-${format(new Date(), DATE_ISO_FORMAT)}.csv`);
                  }
                }}
              >
                {t`resources.databases.exportCurrentView`}
              </ActionButton>
            </>
          )}
        </ToolbarSectionButtons>
      </ToolbarSection>
      <LoaderWrapper isLoading={isPending} loader={<ResourceDatabaseTableSkeleton />}>
        {items.Data?.length ? (
          <>
            <ResourceDatabaseItemsTable
              items={items.Data}
              sortingState={sortingState}
              onSort={onSort}
              selectedItems={selectedItems}
              toggleItem={toggleItem}
              toggleAll={toggleAll}
              showBulkSelect={menuType === UserType.client}
              columnsToShow={columnsToInclude}
              openEditItemModal={openEditItemModal}
              openDeleteItemModal={openDeleteItemModal}
              openPreview={item => dispatch(previewDatabaseViewItem(item))}
              getAccessType={getAccessType}
              sortableColumns={sortableColumns}
            />
            <BottomSection>
              <PaginationStyled
                page={queryParams.pageNum - 1}
                pageCount={pageCount}
                options={[
                  {
                    value: 0,
                    label: `${queryParams.pageNum}/${pageCount}`,
                  },
                ]}
                defaultValue={0}
                onLeftClick={createOnPageChange('decrease')}
                onRightClick={createOnPageChange('increase')}
              />
            </BottomSection>
          </>
        ) : (
          <ResourcesNoData label={t`resources.content.noItems`} />
        )}
      </LoaderWrapper>
    </>
  );
};

export { ResourceDatabaseItemsContainer };

const databaseViewQueryParamsSelector = createSelector(selectDatabaseViewQueryParams, queryParams => ({
  queryParams,
}));

const useDatabaseViewQueryParams = () => {
  const { queryParams } = useAppSelector(databaseViewQueryParamsSelector);
  const dispatch = useAppDispatch();
  const setQueryParams = useCallback(
    (queryParams: IDatabaseViewQueryParams) => {
      dispatch(setDatabaseViewQueryParams(queryParams));
    },
    [dispatch],
  );

  return { queryParams, setQueryParams };
};

const ToolbarSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;

  input {
    background-image: url(${Search});
    background-repeat: no-repeat;
    background-position: 95% center;
    background-size: 16px;
    max-width: 356px;
    height: 36px;
  }
`;

const BottomSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PaginationStyled = styled(PaginationSelectGroup)`
  ${media.phone} {
    width: 190px;
  }
`;

const ToolbarSectionButtons = styled.div`
  display: flex;
  column-gap: 8px;
  flex: 1;
  justify-content: flex-end;
  flex-wrap: wrap;

  ${media.tablet} {
    flex-wrap: nowrap;
  }
`;

const ActionButton = styled(ButtonBase)`
  background-color: #fff;
  color: ${otherColors.lxc.blue};
  height: 30px;
  border: 1px solid ${otherColors.lxc.blue};
  font-weight: 500;
  font-size: 12px;
  padding: 0 12px;
`;
